import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspaceTabNavigation } from '../context/WorkspaceTabsContext';
import ticketService from '../services/ticketService';
import { ArrowLeftIcon } from '../components/Icons';

const splitLines = (value) => String(value || '')
  .split(/\r?\n/)
  .map(item => item.trim())
  .filter(Boolean);

const normalizeWorkflow = (data = {}) => ({
  stages: (data.stages || []).map(stage => ({
    ...stage,
    checklist_text: (stage.checklist || []).join('\n')
  })),
  statuses: data.statuses || [],
  actions: (data.actions || []).map(action => ({
    ...action,
    allowed_stages: action.allowed_stages || [],
    requires_assignee: !!action.requires_assignee,
    requires_note: !!action.requires_note,
    requires_close_permission: !!action.requires_close_permission,
    is_active: action.is_active !== false
  }))
});

const sectionTitleStyle = {
  margin: 0,
  color: 'var(--text-primary)',
  fontSize: '1rem',
  fontWeight: 800
};

const labelStyle = {
  display: 'block',
  marginBottom: '0.35rem',
  fontSize: '0.75rem',
  fontWeight: 800,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase'
};

const chipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: '32px',
  border: '1px solid var(--border-light)',
  borderRadius: '8px',
  padding: '0.35rem 0.6rem',
  backgroundColor: 'var(--bg-soft)',
  color: 'var(--text-secondary)',
  fontSize: '0.78rem',
  fontWeight: 700,
  wordBreak: 'break-word'
};

const toggleLabelStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.45rem',
  fontSize: '0.82rem',
  color: 'var(--text-secondary)',
  fontWeight: 700
};

const TicketWorkflowSettings = () => {
  const navigate = useNavigate();
  const { closeCurrentTabAndNavigate } = useWorkspaceTabNavigation();
  const { hasPermission, getDefaultRoute } = useAuth();
  const canView = hasPermission('tickets', 'view');
  const canEdit = hasPermission('tickets', 'edit');

  const [workflow, setWorkflow] = useState({ stages: [], statuses: [], actions: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchWorkflow = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    const res = await ticketService.getWorkflow();
    if (res.success) {
      setWorkflow(normalizeWorkflow(res.data));
    } else {
      alert(res.message || 'Failed to fetch workflow');
    }
    setLoading(false);
  }, [canView]);

  useEffect(() => {
    if (!canView) {
      navigate(getDefaultRoute(), { replace: true });
      return;
    }
    fetchWorkflow();
  }, [canView, fetchWorkflow, getDefaultRoute, navigate]);

  const stageLabels = useMemo(() => Object.fromEntries(
    workflow.stages.map(stage => [stage.value, stage.label])
  ), [workflow.stages]);

  const updateStage = (index, field, value) => {
    setWorkflow(prev => ({
      ...prev,
      stages: prev.stages.map((stage, stageIndex) => (
        stageIndex === index ? { ...stage, [field]: value } : stage
      ))
    }));
  };

  const updateStatus = (index, field, value) => {
    setWorkflow(prev => ({
      ...prev,
      statuses: prev.statuses.map((status, statusIndex) => (
        statusIndex === index ? { ...status, [field]: value } : status
      ))
    }));
  };

  const updateAction = (index, field, value) => {
    setWorkflow(prev => ({
      ...prev,
      actions: prev.actions.map((action, actionIndex) => (
        actionIndex === index ? { ...action, [field]: value } : action
      ))
    }));
  };

  const toggleActionStage = (index, stageValue, checked) => {
    setWorkflow(prev => ({
      ...prev,
      actions: prev.actions.map((action, actionIndex) => {
        if (actionIndex !== index) return action;
        const currentStages = action.allowed_stages || [];
        const nextStages = checked
          ? [...new Set([...currentStages, stageValue])]
          : currentStages.filter(value => value !== stageValue);
        return { ...action, allowed_stages: nextStages };
      })
    }));
  };

  const buildPayload = () => ({
    stages: workflow.stages.map(({ checklist_text, ...stage }, index) => ({
      ...stage,
      checklist: splitLines(checklist_text),
      sort_order: index
    })),
    statuses: workflow.statuses.map((status, index) => ({
      ...status,
      sort_order: index
    })),
    actions: workflow.actions.map((action, index) => ({
      ...action,
      allowed_stages: action.allowed_stages || [],
      sort_order: index
    }))
  });

  const saveWorkflow = async () => {
    if (!canEdit) return;
    setSaving(true);
    const res = await ticketService.updateWorkflow(buildPayload());
    setSaving(false);
    if (res.success) {
      setWorkflow(normalizeWorkflow(res.data));
      alert('Workflow saved');
    } else {
      alert(res.message || 'Failed to save workflow');
    }
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading workflow...</div>;
  }

  return (
    <div style={{ maxWidth: '1240px', margin: '0 auto', padding: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'flex-start' }}>
          <button className="btn" onClick={() => closeCurrentTabAndNavigate('/tickets')} style={{ padding: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ArrowLeftIcon />
          </button>
          <div>
            <h2 style={{ margin: 0, color: 'var(--text-primary)' }}>Ticket Workflow</h2>
            <p style={{ margin: '0.25rem 0 0 0', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              Set the labels, action routing, and requirements used by merchant setup tickets.
            </p>
          </div>
        </div>
        <button className="btn btn-primary" onClick={saveWorkflow} disabled={!canEdit || saving}>
          {saving ? 'Saving...' : 'Save Workflow'}
        </button>
      </div>

      {!canEdit && (
        <div className="card" style={{ padding: '0.85rem 1rem', marginBottom: '1rem', color: 'var(--text-secondary)' }}>
          Edit permission is required to change workflow settings.
        </div>
      )}

      <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={sectionTitleStyle}>Stages</h3>
        </div>
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {workflow.stages.map((stage, index) => (
            <div key={stage.value} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', alignItems: 'start', borderTop: index === 0 ? 'none' : '1px solid var(--border-light)', paddingTop: index === 0 ? 0 : '0.75rem' }}>
              <div>
                <label style={labelStyle}>Key</label>
                <span style={chipStyle}>{stage.value}</span>
              </div>
              <div>
                <label style={labelStyle}>Label</label>
                <input className="input" value={stage.label || ''} disabled={!canEdit} onChange={(e) => updateStage(index, 'label', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Checklist</label>
                <textarea className="input" rows="4" value={stage.checklist_text || ''} disabled={!canEdit} onChange={(e) => updateStage(index, 'checklist_text', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Active</label>
                <label style={toggleLabelStyle}>
                  <input type="checkbox" checked={stage.is_active !== false} disabled={!canEdit} onChange={(e) => updateStage(index, 'is_active', e.target.checked)} />
                  Yes
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
        <h3 style={{ ...sectionTitleStyle, marginBottom: '1rem' }}>Statuses</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' }}>
          {workflow.statuses.map((status, index) => (
            <div key={status.value} style={{ border: '1px solid var(--border-light)', borderRadius: '8px', padding: '0.85rem', backgroundColor: 'var(--bg-soft)' }}>
              <label style={labelStyle}>Key</label>
              <span style={chipStyle}>{status.value}</span>
              <div style={{ marginTop: '0.75rem' }}>
                <label style={labelStyle}>Label</label>
                <input className="input" value={status.label || ''} disabled={!canEdit} onChange={(e) => updateStatus(index, 'label', e.target.value)} />
              </div>
              <label style={{ ...toggleLabelStyle, marginTop: '0.75rem' }}>
                <input type="checkbox" checked={status.is_active !== false} disabled={!canEdit} onChange={(e) => updateStatus(index, 'is_active', e.target.checked)} />
                Active
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: '1.25rem' }}>
        <h3 style={{ ...sectionTitleStyle, marginBottom: '1rem' }}>Actions</h3>
        <div style={{ display: 'grid', gap: '1rem' }}>
          {workflow.actions.map((action, index) => (
            <div key={action.key} style={{ border: '1px solid var(--border-light)', borderRadius: '8px', padding: '1rem', backgroundColor: action.variant === 'danger' ? '#fff7f7' : 'var(--bg-card)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                <div>
                  <div style={{ ...chipStyle, backgroundColor: 'var(--bg-soft)' }}>{action.key}</div>
                  <div style={{ marginTop: '0.45rem', fontWeight: 800, color: 'var(--text-primary)' }}>{action.label || action.key}</div>
                </div>
                <label style={toggleLabelStyle}>
                  <input type="checkbox" checked={action.is_active !== false} disabled={!canEdit} onChange={(e) => updateAction(index, 'is_active', e.target.checked)} />
                  Active
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div>
                  <label style={labelStyle}>Button Label</label>
                  <input className="input" value={action.label || ''} disabled={!canEdit} onChange={(e) => updateAction(index, 'label', e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Variant</label>
                  <select className="input" value={action.variant || 'secondary'} disabled={!canEdit} onChange={(e) => updateAction(index, 'variant', e.target.value)}>
                    <option value="primary">Primary</option>
                    <option value="secondary">Secondary</option>
                    <option value="danger">Danger</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Next Stage</label>
                  <select className="input" value={action.next_stage || ''} disabled={!canEdit} onChange={(e) => updateAction(index, 'next_stage', e.target.value)}>
                    <option value="">No change</option>
                    {workflow.stages.map(stage => <option key={stage.value} value={stage.value}>{stage.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Next Status</label>
                  <select className="input" value={action.next_status || ''} disabled={!canEdit} onChange={(e) => updateAction(index, 'next_status', e.target.value)}>
                    <option value="">No change</option>
                    {workflow.statuses.map(status => <option key={status.value} value={status.value}>{status.label}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '0.75rem' }}>
                <label style={labelStyle}>Description</label>
                <textarea className="input" rows="2" value={action.description || ''} disabled={!canEdit} onChange={(e) => updateAction(index, 'description', e.target.value)} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
                <div>
                  <label style={labelStyle}>Available From</label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {workflow.stages.map(stage => (
                      <label key={`${action.key}-${stage.value}`} style={{ ...toggleLabelStyle, border: '1px solid var(--border-light)', borderRadius: '8px', padding: '0.35rem 0.55rem', backgroundColor: 'var(--bg-soft)' }}>
                        <input type="checkbox" checked={(action.allowed_stages || []).includes(stage.value)} disabled={!canEdit} onChange={(e) => toggleActionStage(index, stage.value, e.target.checked)} />
                        {stageLabels[stage.value] || stage.value}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Requirements</label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <label style={{ ...toggleLabelStyle, border: '1px solid var(--border-light)', borderRadius: '8px', padding: '0.35rem 0.55rem', backgroundColor: 'var(--bg-soft)' }}>
                      <input type="checkbox" checked={!!action.requires_assignee} disabled={!canEdit} onChange={(e) => updateAction(index, 'requires_assignee', e.target.checked)} />
                      Assignee
                    </label>
                    <label style={{ ...toggleLabelStyle, border: '1px solid var(--border-light)', borderRadius: '8px', padding: '0.35rem 0.55rem', backgroundColor: 'var(--bg-soft)' }}>
                      <input type="checkbox" checked={!!action.requires_note} disabled={!canEdit} onChange={(e) => updateAction(index, 'requires_note', e.target.checked)} />
                      Note
                    </label>
                    <label style={{ ...toggleLabelStyle, border: '1px solid var(--border-light)', borderRadius: '8px', padding: '0.35rem 0.55rem', backgroundColor: 'var(--bg-soft)' }}>
                      <input type="checkbox" checked={!!action.requires_close_permission} disabled={!canEdit} onChange={(e) => updateAction(index, 'requires_close_permission', e.target.checked)} />
                      Close permission
                    </label>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TicketWorkflowSettings;
